import React from "react";
import { Badge } from "@/components/ui/badge";
import { FilterResponse } from "modules/catalog/types/filterResponse";
import { filterParam } from "modules/catalog/types/filters_params";

/** Available sort options for catalog search */
export type SortOption = "relevance" | "lower_price" | "higher_price";

/** Sort option label map */
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: "relevance", label: "Relevancia" },
    { value: "lower_price", label: "Precio más bajo" },
    { value: "higher_price", label: "Precio más alto" },
];

/**
 * Props for the FilterBadgeRow component.
 * Pure presentational — no routing or navigation dependencies.
 */
export interface FilterBadgeRowProps {
    /** Available filter groups returned from API (brand, tag, etc.) */
    availableFilters: FilterResponse[];
    /** Currently active filter params (name + active values) */
    activeFilters: filterParam[];
    /** Currently active sort key */
    sortBy: SortOption;
    /**
     * Called when user toggles a specific filter value.
     * @param filterName - The filter group name (e.g. "brand")
     * @param value - The component display_name being toggled
     */
    onFilterToggle: (filterName: string, value: string) => void;
    /**
     * Called when user selects a sort option.
     * @param sort - The new sort value
     */
    onSortChange: (sort: SortOption) => void;
    /** Called when user clears all active filters */
    onClearAll: () => void;
}

/**
 * Horizontal scrollable strip of sort and filter badges.
 * Renders sort options always, and filter options from `availableFilters`.
 * Provides a clear-all button when at least one filter is active.
 *
 * @param props - See {@link FilterBadgeRowProps}
 */
export const FilterBadgeRow: React.FC<FilterBadgeRowProps> = ({
    availableFilters,
    activeFilters,
    sortBy,
    onFilterToggle,
    onSortChange,
    onClearAll,
}) => {
    const hasActiveFilters = activeFilters.some((f) => f.value.length > 0);

    return (
        <div className="flex flex-row gap-2 overflow-x-auto pb-1 flex-shrink-0">
            {/* Sort badges */}
            {SORT_OPTIONS.map(({ value, label }) => (
                <Badge
                    key={value}
                    variant={sortBy === value ? "default" : "outline"}
                    className="cursor-pointer whitespace-nowrap"
                    onClick={() => onSortChange(value)}
                >
                    {label}
                </Badge>
            ))}

            {/* Dynamic filter badges from API */}
            {availableFilters.map((filterGroup) => {
                const activeEntry = activeFilters.find((f) => f.name === filterGroup.type);
                return filterGroup.component.map((comp) => {
                    const isActive = activeEntry?.value.includes(comp.display_name) ?? false;
                    return (
                        <Badge
                            key={`${filterGroup.type}-${comp.key}`}
                            variant={isActive ? "default" : "outline"}
                            className="cursor-pointer whitespace-nowrap"
                            onClick={() => onFilterToggle(filterGroup.type, comp.display_name)}
                        >
                            {comp.display_name}
                        </Badge>
                    );
                });
            })}

            {/* Clear-all button — only visible when filters are active */}
            {hasActiveFilters && (
                <button
                    type="button"
                    aria-label="Clear all filters"
                    className="text-xs text-muted-foreground underline whitespace-nowrap shrink-0 px-1"
                    onClick={onClearAll}
                >
                    Limpiar
                </button>
            )}
        </div>
    );
};
